/**
 * Auto-Link Preview Dialog
 *
 * Shows a table of auto-link matches above 85% confidence with
 * checkboxes to exclude entries, then confirms and links them.
 */

import React, { useState, useEffect } from 'react';
import { Zap, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import roleMasterService, { BulkAutoLinkResponse } from '@/services/api/roleMasterService';

interface AutoLinkPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

const AutoLinkPreviewDialog: React.FC<AutoLinkPreviewDialogProps> = ({
  open,
  onOpenChange,
  onComplete,
}) => {
  const [autoLinkPreview, setAutoLinkPreview] = useState<BulkAutoLinkResponse | null>(null);
  const [excludedFromAutoLink, setExcludedFromAutoLink] = useState<Set<number>>(new Set());
  const [isAutoLinking, setIsAutoLinking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch preview when dialog opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setExcludedFromAutoLink(new Set());
      roleMasterService.autoLink(0.85, true)
        .then((preview) => {
          setAutoLinkPreview(preview);
        })
        .catch((error) => {
          console.error('Auto-link preview failed:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setAutoLinkPreview(null);
    }
  }, [open]);

  const toggleAutoLinkExclusion = (entryId: number) => {
    setExcludedFromAutoLink(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const selectedAutoLinkCount = autoLinkPreview
    ? autoLinkPreview.details.filter(d => !excludedFromAutoLink.has(d.role_master_entry_id)).length
    : 0;

  const handleAutoLinkConfirm = async () => {
    if (!autoLinkPreview) return;
    setIsAutoLinking(true);
    try {
      const toLink = autoLinkPreview.details.filter(d => !excludedFromAutoLink.has(d.role_master_entry_id));
      let linked = 0;
      for (const detail of toLink) {
        try {
          await roleMasterService.linkJob(detail.role_master_entry_id, detail.job_id, detail.fo_title_id, false);
          linked++;
        } catch (e) {
          console.error(`Failed to link ${detail.rm_title}:`, e);
        }
      }
      onOpenChange(false);
      onComplete();
    } catch (error) {
      console.error('Auto-link failed:', error);
    } finally {
      setIsAutoLinking(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!open || (!autoLinkPreview && !isLoading)) return null;

  if (isLoading) {
    return (
      <Card className="border-heritage-blue/30 bg-heritage-blue/5">
        <CardContent className="py-8 text-center text-gray-400">
          <Zap className="h-5 w-5 animate-pulse inline mr-2 text-heritage-blue" />
          Matching roles...
        </CardContent>
      </Card>
    );
  }

  if (!autoLinkPreview) return null;

  return (
    <Card className="border-heritage-blue/30 bg-heritage-blue/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-5 w-5 text-heritage-blue" />
          Auto-Link Preview — {autoLinkPreview.details.length} matches above 85% confidence
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-[300px] overflow-auto border rounded mb-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <input
                    type="checkbox"
                    checked={excludedFromAutoLink.size === 0}
                    onChange={() => {
                      if (excludedFromAutoLink.size === 0) {
                        setExcludedFromAutoLink(new Set(autoLinkPreview.details.map(d => d.role_master_entry_id)));
                      } else {
                        setExcludedFromAutoLink(new Set());
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                </TableHead>
                <TableHead className="whitespace-nowrap">RM Title</TableHead>
                <TableHead className="whitespace-nowrap">F&O Job ID</TableHead>
                <TableHead className="whitespace-nowrap">F&O Title</TableHead>
                <TableHead className="whitespace-nowrap">Confidence</TableHead>
                <TableHead className="whitespace-nowrap">Mismatch</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {autoLinkPreview.details.map((d) => {
                const excluded = excludedFromAutoLink.has(d.role_master_entry_id);
                return (
                  <TableRow key={d.role_master_entry_id} className={cn(excluded && 'opacity-40')}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={!excluded}
                        onChange={() => toggleAutoLinkExclusion(d.role_master_entry_id)}
                        className="rounded border-gray-300"
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{d.rm_title}</TableCell>
                    <TableCell className="font-mono text-sm whitespace-nowrap">{d.job_id}</TableCell>
                    <TableCell className="whitespace-nowrap">{d.fo_title_id}</TableCell>
                    <TableCell className="whitespace-nowrap">{(d.confidence * 100).toFixed(0)}%</TableCell>
                    <TableCell>{d.title_mismatch ? <Badge className="bg-amber-100 text-amber-700 text-xs">Yes</Badge> : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleAutoLinkConfirm} disabled={isAutoLinking || selectedAutoLinkCount === 0}>
            <CheckCircle className="h-4 w-4 mr-1" />
            {isAutoLinking ? 'Linking...' : `Confirm & Link ${selectedAutoLinkCount} Roles`}
          </Button>
          <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
          <Button
            variant="outline" size="sm"
            onClick={() => {
              const mismatchIds = new Set(
                autoLinkPreview.details.filter(d => d.title_mismatch).map(d => d.role_master_entry_id)
              );
              setExcludedFromAutoLink(prev => new Set([...prev, ...mismatchIds]));
            }}
            className="text-xs"
          >
            Deselect Mismatches ({autoLinkPreview.details.filter(d => d.title_mismatch).length})
          </Button>
          {excludedFromAutoLink.size > 0 && (
            <span className="text-sm text-gray-500">
              {excludedFromAutoLink.size} excluded
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoLinkPreviewDialog;
